import {
  Alert,
  Button,
  Card,
  Col,
  Dropdown,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import PropTypes from "prop-types";
import SettingsHeader from "./SettingsHeader";
import axios from "../../utils/axios";

const { Text } = Typography;

export const errorMessage = (e) =>
  e?.response?.data?.error ||
  e?.response?.data?.message ||
  e?.message ||
  "Erreur inconnue";

export const STATUS_OPTIONS = [
  { value: "online", label: "En ligne" },
  { value: "offline", label: "Hors ligne" },
  { value: "inactive", label: "Inactif" },
  { value: "degraded", label: "Dégradé" },
  { value: "error", label: "Erreur" },
  { value: "unenrolled", label: "Désenrôlé" },
  { value: "unknown", label: "Inconnu" },
];

const STATUS_META = {
  online: { label: "En ligne", color: "green" },
  offline: { label: "Hors ligne", color: "default" },
  inactive: { label: "Inactif", color: "orange" },
  degraded: { label: "Dégradé", color: "red" },
  error: { label: "Erreur", color: "red" },
  unenrolled: { label: "Désenrôlé", color: "default" },
};

/* Cle de statut normalisee (null -> "unknown"). */
export const statusKey = (status) => status || "unknown";

/* Statut Fleet -> libelle et couleur antd ; statut non repertorie affiche tel quel. */
export const statusMeta = (status) =>
  STATUS_META[status] || { label: status || "Inconnu", color: "default" };

/* Duree relative en francais (« il y a 5 min »). */
export const relativeTime = (iso, now = Date.now()) => {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 60) return "à l'instant";
  const m = Math.round(s / 60);
  if (m < 60) return `il y a ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `il y a ${h} h`;
  return `il y a ${Math.round(h / 24)} j`;
};

export const serverLabel = (s) =>
  [s.hostname, s.comment].filter(Boolean).join(" - ");

/* Filtre pur (testable) sur la liste des lignes. */
export const filterAgents = (
  agents,
  {
    search = "",
    unassignedOnly = false,
    missingFleetOnly = false,
    notInExploitOnly = false,
    notOnlineOnly = false,
    status,
  } = {}
) => {
  const q = search.trim().toLowerCase();
  return agents.filter((a) => {
    if (q) {
      const hay = [
        a.agent,
        a.hostname,
        a.server && a.server.hostname,
        a.server && a.server.comment,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (unassignedOnly && a.server) return false;
    if (missingFleetOnly && a.inFleet) return false;
    if (notInExploitOnly && a.inExploit) return false;
    if (notOnlineOnly && statusKey(a.status) === "online") return false;
    if (status && statusKey(a.status) !== status) return false;
    return true;
  });
};

/* Compteurs du recapitulatif de sante (sur la liste complete, hors filtres). */
export const summarize = (agents) => ({
  total: agents.length,
  notInExploit: agents.filter((a) => !a.inExploit).length,
  notInFleet: agents.filter((a) => !a.inFleet).length,
  unassigned: agents.filter((a) => !a.server).length,
  notOnline: agents.filter((a) => statusKey(a.status) !== "online").length,
});

/* Tuiles du recapitulatif ; un clic bascule le filtre associe. */
export const SUMMARY_TILES = [
  { key: "total", label: "Agents" },
  { key: "notInExploit", label: "Absents d'exploit", filter: "notInExploitOnly" },
  { key: "notInFleet", label: "Absents de Fleet", filter: "missingFleetOnly" },
  { key: "unassigned", label: "Sans serveur", filter: "unassignedOnly" },
  { key: "notOnline", label: "Pas en ligne", filter: "notOnlineOnly", needsFleet: true },
];

/* Comparaison texte insensible a la casse ; valeurs vides en dernier. */
export const compareText = (a, b) => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return String(a).localeCompare(String(b), "fr", { sensitivity: "base" });
};

/* Tris par colonne (toutes sauf Actions). */
export const SORTERS = {
  agent: (a, b) => compareText(a.agent, b.agent),
  hostname: (a, b) => compareText(a.hostname, b.hostname),
  inExploit: (a, b) => Number(!!b.inExploit) - Number(!!a.inExploit),
  inFleet: (a, b) => Number(!!b.inFleet) - Number(!!a.inFleet),
  server: (a, b) =>
    compareText(a.server && serverLabel(a.server), b.server && serverLabel(b.server)),
  status: (a, b) =>
    compareText(statusMeta(a.status).label, statusMeta(b.status).label) ||
    compareText(b.lastCheckin, a.lastCheckin),
};

/* Seuil d'inactivite par defaut (jours) des regles de nettoyage. */
export const DEFAULT_STALE_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;

/* Anciennete du dernier check-in Fleet en jours (null si inconnue). */
export const checkinAgeDays = (iso, now = Date.now()) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : Math.floor((now - t) / DAY_MS);
};

/*
 * Regles de nettoyage, par priorite decroissante de suppression : moins un
 * agent sert (present nulle part ailleurs, sans serveur, inactif) et plus sa
 * suppression est sure, plus il passe devant. Un agent prend la premiere regle
 * qui s'applique. « stale » = present dans Fleet, pas en ligne, dernier
 * check-in connu et plus vieux que le seuil.
 * phase "v1" : action disponible ; "v2" / "v1+v2" : suppression Fleet, disponible
 * seulement si la cle d'ecriture Fleet est configuree (fleetWriteEnabled).
 * Un agent « unenrolled » est traite comme absent de Fleet.
 */
export const CLEANUP_RULES = [
  {
    key: "exploitOrphan",
    title: "Orphelin exploit",
    conditions: ["Exploit : oui", "Serveur : aucun", "Fleet : absent"],
    action: "Retirer de exploit",
    phase: "v1",
    color: "red",
    match: (a) => a.inExploit && !a.server && !a.inFleet,
  },
  {
    key: "fleetGhost",
    title: "Fantôme Fleet",
    conditions: [
      "Exploit : non",
      "Serveur : aucun",
      "Fleet : inactif > seuil ou désenrôlé",
    ],
    action: "Désenrôler de Fleet",
    phase: "v2",
    color: "red",
    match: (a, stale) => !a.inExploit && !a.server && (stale || !!a.unenrolledGhost),
  },
  {
    key: "abandoned",
    title: "Abandonné",
    conditions: ["Exploit : oui", "Serveur : aucun", "Fleet : inactif > seuil"],
    action: "Désenrôler et retirer de exploit",
    phase: "v1+v2",
    color: "volcano",
    match: (a, stale) => a.inExploit && !a.server && stale,
  },
  {
    key: "dormant",
    title: "En sommeil",
    conditions: ["Serveur : aucun", "Fleet : pas en ligne ≤ seuil", "ou durée inconnue"],
    action: "Surveiller, ne pas supprimer",
    phase: "watch",
    color: "orange",
    match: (a) => !a.server && statusKey(a.status) !== "online",
  },
  {
    key: "serverSilent",
    title: "Serveur muet",
    conditions: ["Serveur : assigné", "Fleet : absent ou inactif > seuil"],
    action: "Vérifier le serveur, réaffecter si réinstallé",
    phase: "check",
    color: "gold",
    match: (a, stale) => !!a.server && (!a.inFleet || stale),
  },
  {
    key: "toAssign",
    title: "À affecter",
    conditions: ["Serveur : aucun", "Fleet : en ligne"],
    action: "Affecter à un serveur",
    phase: "v1",
    color: "blue",
    match: (a) => !a.server && statusKey(a.status) === "online",
  },
];

const PHASE_META = {
  v1: { label: "Disponible", color: "green" },
  watch: { label: "Aucune action", color: "default" },
  check: { label: "Contrôle manuel", color: "default" },
};

const FLEET_PHASE_OFF = { label: "Clé Fleet non configurée", color: "default" };

/* Phase affichee d'une regle ; les phases Fleet dependent de la cle d'ecriture. */
export const phaseMeta = (phase, fleetWriteEnabled) => {
  if (phase === "v2" || phase === "v1+v2") {
    return fleetWriteEnabled ? PHASE_META.v1 : FLEET_PHASE_OFF;
  }
  return PHASE_META[phase] || PHASE_META.watch;
};

/* Raison lisible d'un refus de suppression (null = autorisee). */
export const deleteBlockReason = (row, scope) => {
  if (scope === "exploit") {
    if (!row.inExploit) return "Agent déjà absent de exploit";
    if (row.server) return "Assigné à un serveur : désaffectez d'abord";
    return null;
  }
  if (!row.inFleet) return "Absent de Fleet";
  if (scope === "all" && !row.inExploit) return "Absent de exploit";
  if (row.server) return "Assigné à un serveur : désaffectez d'abord";
  if (statusKey(row.status) === "online") {
    return "Agent en ligne : il tourne encore sur une machine";
  }
  return null;
};

/* Texte a saisir pour confirmer : hostname, sinon 8 premiers caracteres de l'id. */
export const confirmToken = (row) => row.hostname || String(row.agent).substring(0, 8);

/* Regle de nettoyage d'un agent (null = sain), avec son rang de priorite. */
export const classifyAgent = (a, staleDays = DEFAULT_STALE_DAYS, now = Date.now()) => {
  const age = checkinAgeDays(a.lastCheckin, now);
  const unenrolled = statusKey(a.status) === "unenrolled";
  const inFleetEff = !!a.inFleet && !unenrolled;
  const stale =
    inFleetEff && statusKey(a.status) !== "online" && age !== null && age > staleDays;
  const eff = { ...a, inFleet: inFleetEff, unenrolledGhost: !!a.inFleet && unenrolled };
  const idx = CLEANUP_RULES.findIndex((r) => r.match(eff, stale));
  return idx === -1 ? null : { ...CLEANUP_RULES[idx], rank: idx + 1, age };
};

const DEFAULT_WIDTHS = {
  agent: 150,
  hostname: 200,
  inExploit: 120,
  inFleet: 110,
  server: 240,
  status: 170,
  rule: 170,
  actions: 340,
};
const MIN_WIDTH = 80;

/* En-tete de colonne redimensionnable par glisser sur son bord droit. */
export function ResizableTitle({ onResize, width, ...rest }) {
  if (!onResize || !width) return <th {...rest} />;
  const start = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const move = (ev) =>
      onResize(Math.max(MIN_WIDTH, width + ev.clientX - startX));
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };
  return (
    <th {...rest} style={{ ...rest.style, position: "relative" }}>
      {rest.children}
      <span
        role="separator"
        aria-label="Redimensionner la colonne"
        onMouseDown={start}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          top: 0,
          right: -4,
          bottom: 0,
          width: 8,
          cursor: "col-resize",
          zIndex: 1,
        }}
      />
    </th>
  );
}

ResizableTitle.propTypes = {
  onResize: PropTypes.func,
  width: PropTypes.number,
  style: PropTypes.object,
  children: PropTypes.node,
};

const YesNo = ({ value }) =>
  value ? <Tag color="green">Oui</Tag> : <Tag>Non</Tag>;

YesNo.propTypes = { value: PropTypes.bool };

function AgentId({ id }) {
  const short = id.length > 8 ? `${id.substring(0, 8)}…` : id;
  return (
    <Tooltip title={id}>
      <Text copyable={{ text: id }} code>
        {short}
      </Text>
    </Tooltip>
  );
}

AgentId.propTypes = { id: PropTypes.string.isRequired };

function ElasticAgents() {
  const [agents, setAgents] = useState([]);
  const [servers, setServers] = useState([]);
  const [fleet, setFleet] = useState({ available: true, error: null });
  const [fleetWrite, setFleetWrite] = useState(false);
  const [deleting, setDeleting] = useState(null); // { row, scope } suppression Fleet
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState({}); // agent -> true pendant une action
  const [search, setSearch] = useState("");
  // filtres pilotes par les tuiles du recapitulatif
  const [flags, setFlags] = useState({});
  const [status, setStatus] = useState();
  const [assigning, setAssigning] = useState(null); // ligne en cours d'affectation
  const [serverId, setServerId] = useState();
  const [widths, setWidths] = useState(DEFAULT_WIDTHS);
  const [staleDays, setStaleDays] = useState(DEFAULT_STALE_DAYS);
  const [ruleFilter, setRuleFilter] = useState(); // cle de regle de nettoyage

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/api/agents/inventory");
      const data = (res && res.data) || {};
      setAgents(
        Array.isArray(data.agents) ? data.agents.filter((a) => a && a.agent) : []
      );
      setFleet({
        available: data.fleetAvailable !== false,
        error: data.fleetError || null,
      });
      setFleetWrite(data.fleetWriteEnabled === true);
    } catch (e) {
      message.error(`Chargement des agents impossible : ${errorMessage(e)}`);
    }
    setLoading(false);
  }, []);

  const loadServers = useCallback(async () => {
    try {
      const res = await axios.get("/api/servers");
      setServers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      message.error(`Chargement des serveurs impossible : ${errorMessage(e)}`);
    }
  }, []);

  useEffect(() => {
    load();
    loadServers();
  }, [load, loadServers]);

  const replaceRow = (row) => {
    if (!row || !row.agent) return;
    setAgents((prev) => prev.map((a) => (a.agent === row.agent ? row : a)));
  };

  const withBusy = async (agent, fn) => {
    setBusy((b) => ({ ...b, [agent]: true }));
    try {
      await fn();
    } finally {
      setBusy((b) => ({ ...b, [agent]: false }));
    }
  };

  const handleHealth = (row) =>
    withBusy(row.agent, async () => {
      try {
        const res = await axios.get(
          `/api/agents/${encodeURIComponent(row.agent)}/health`
        );
        replaceRow(res.data && res.data.agent);
        if (res.data && typeof res.data.fleetWriteEnabled === "boolean") {
          setFleetWrite(res.data.fleetWriteEnabled);
        }
        if (res.data && res.data.fleetAvailable === false) {
          message.warning(
            `Fleet indisponible${
              res.data.fleetError ? ` : ${res.data.fleetError}` : ""
            }`
          );
        } else {
          message.success("Santé de l'agent actualisée");
        }
      } catch (e) {
        message.error(`Vérification impossible : ${errorMessage(e)}`);
      }
    });

  const putServer = (row, id, replace) =>
    axios.put(`/api/agents/${encodeURIComponent(row.agent)}/server`, {
      serverId: id,
      replace,
    });

  const closeAssign = () => {
    setAssigning(null);
    setServerId(undefined);
  };

  const handleAssign = async () => {
    const row = assigning;
    if (!row || serverId === undefined) return;
    const done = (res) => {
      replaceRow(res.data && res.data.agent);
      message.success("Agent affecté au serveur");
      closeAssign();
      loadServers();
    };
    await withBusy(row.agent, async () => {
      try {
        done(await putServer(row, serverId, false));
      } catch (e) {
        if (e?.response?.status === 409) {
          Modal.confirm({
            title: "Remplacer l'agent du serveur ?",
            content: `${errorMessage(
              e
            )} Confirmez-vous le remplacement de l'agent actuel de ce serveur ?`,
            okText: "Remplacer",
            cancelText: "Annuler",
            okButtonProps: { danger: true },
            onOk: async () => {
              try {
                done(await putServer(row, serverId, true));
                // l'ancien agent change aussi de statut : relecture complete
                load();
              } catch (e2) {
                message.error(`Affectation impossible : ${errorMessage(e2)}`);
              }
            },
          });
        } else {
          message.error(`Affectation impossible : ${errorMessage(e)}`);
        }
      }
    });
  };

  const handleUnassign = (row) =>
    withBusy(row.agent, async () => {
      try {
        const res = await axios.delete(
          `/api/agents/${encodeURIComponent(row.agent)}/server`
        );
        replaceRow(res.data && res.data.agent);
        message.success("Agent désaffecté du serveur");
        loadServers();
      } catch (e) {
        message.error(`Désaffectation impossible : ${errorMessage(e)}`);
      }
    });

  const handleRemove = (row) =>
    withBusy(row.agent, async () => {
      try {
        await axios.delete(`/api/agents/${encodeURIComponent(row.agent)}`);
        message.success("Agent retiré de exploit");
        await load();
      } catch (e) {
        message.error(`Retrait impossible : ${errorMessage(e)}`);
      }
    });

  const closeDelete = () => {
    setDeleting(null);
    setConfirmText("");
  };

  const handleFleetDelete = async () => {
    if (!deleting) return;
    const { row, scope } = deleting;
    await withBusy(row.agent, async () => {
      try {
        const res = await axios.delete(
          `/api/agents/${encodeURIComponent(row.agent)}/${scope}`
        );
        const updated = res.data && res.data.agent;
        if (updated && updated.agent) {
          replaceRow(updated);
        } else {
          setAgents((prev) => prev.filter((a) => a.agent !== row.agent));
        }
        message.success(
          scope === "all"
            ? "Agent désenrôlé de Fleet et retiré de exploit"
            : "Agent désenrôlé de Fleet"
        );
        closeDelete();
      } catch (e) {
        message.error(`Désenrôlement impossible : ${errorMessage(e)}`);
        closeDelete();
      }
    });
  };

  const confirmRemove = (row) =>
    Modal.confirm({
      title:
        "Retirer cet agent de exploit ? Les métriques sont conservées ; l'agent réapparaîtra s'il remonte à nouveau des métriques.",
      okText: "Retirer",
      cancelText: "Annuler",
      okButtonProps: { danger: true },
      onOk: () => handleRemove(row),
    });

  const deleteMenu = (row) => {
    const entry = (key, label, scope, onPick) => {
      const reason = deleteBlockReason(row, scope);
      return {
        key,
        disabled: !!reason,
        danger: !reason,
        onClick: onPick,
        label: (
          <span aria-label={reason ? `${label} (${reason})` : label}>
            {label}
            {reason && (
              <>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {reason}
                </Text>
              </>
            )}
          </span>
        ),
      };
    };
    const items = [
      entry("remove", "Retirer de exploit", "exploit", () => confirmRemove(row)),
    ];
    if (fleetWrite) {
      items.push(
        entry("fleet", "Désenrôler de Fleet", "fleet", () =>
          setDeleting({ row, scope: "fleet" })
        ),
        entry("all", "Désenrôler et retirer de exploit", "all", () =>
          setDeleting({ row, scope: "all" })
        )
      );
    } else {
      items.push({
        key: "nokey",
        disabled: true,
        label: "Désenrôlement Fleet : clé non configurée",
      });
    }
    return items;
  };

  // sans Fleet, « absent de Fleet » serait faux pour tous : pas de recommandation
  const rules = useMemo(() => {
    const byAgent = {};
    if (fleet.available) {
      const now = Date.now();
      agents.forEach((a) => {
        byAgent[a.agent] = classifyAgent(a, staleDays, now);
      });
    }
    return byAgent;
  }, [agents, staleDays, fleet.available]);

  const ruleCounts = useMemo(() => {
    const counts = {};
    Object.values(rules).forEach((r) => {
      if (r) counts[r.key] = (counts[r.key] || 0) + 1;
    });
    return counts;
  }, [rules]);

  const filtered = useMemo(() => {
    const list = filterAgents(agents, { search, status, ...flags });
    return ruleFilter
      ? list.filter((a) => rules[a.agent] && rules[a.agent].key === ruleFilter)
      : list;
  }, [agents, search, status, flags, ruleFilter, rules]);

  const summary = useMemo(() => summarize(agents), [agents]);
  const toggleFlag = (name) =>
    setFlags((prev) => ({ ...prev, [name]: !prev[name] }));

  const serverOptions = servers.map((s) => ({
    value: s.key,
    label: `${serverLabel(s)}${s.agent_id ? " (a déjà un agent)" : ""}`,
  }));

  const baseColumns = [
    {
      title: "Agent",
      dataIndex: "agent",
      key: "agent",
      render: (id) => <AgentId id={id} />,
    },
    {
      title: "Hostname",
      dataIndex: "hostname",
      key: "hostname",
      render: (v) => v || <Text type="secondary">-</Text>,
    },
    {
      title: "Dans exploit",
      dataIndex: "inExploit",
      key: "inExploit",
      render: (v) => <YesNo value={!!v} />,
    },
    {
      title: "Dans Fleet",
      dataIndex: "inFleet",
      key: "inFleet",
      render: (v) => <YesNo value={!!v} />,
    },
    {
      title: "Serveur assigné",
      key: "server",
      render: (_, row) =>
        row.server ? (
          serverLabel(row.server)
        ) : (
          <Tag color="orange">Non assigné</Tag>
        ),
    },
    {
      title: "Statut Fleet",
      key: "status",
      render: (_, row) => {
        const meta = statusMeta(row.status);
        return (
          <Space direction="vertical" size={0}>
            <Tag color={meta.color}>{meta.label}</Tag>
            {row.lastCheckin && (
              <Tooltip title={new Date(row.lastCheckin).toLocaleString("fr-FR")}>
                <Text type="secondary">{relativeTime(row.lastCheckin)}</Text>
              </Tooltip>
            )}
            {row.version && <Text type="secondary">v{row.version}</Text>}
          </Space>
        );
      },
    },
    {
      title: "Recommandation",
      key: "rule",
      sorter: (a, b) =>
        ((rules[a.agent] && rules[a.agent].rank) || 99) -
        ((rules[b.agent] && rules[b.agent].rank) || 99),
      render: (_, row) => {
        const r = rules[row.agent];
        if (!fleet.available) return <Text type="secondary">–</Text>;
        if (!r) return <Tag color="green">Sain</Tag>;
        return (
          <Tooltip title={r.action}>
            <Tag color={r.color}>
              {r.rank}. {r.title}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, row) => {
        const isBusy = !!busy[row.agent];
        return (
          <Space size="small" wrap>
            <Button size="small" loading={isBusy} onClick={() => handleHealth(row)}>
              Vérifier
            </Button>
            <Tooltip
              title={
                row.inExploit
                  ? ""
                  : "Agent absent de exploit : il doit d'abord remonter des métriques."
              }
            >
              <span>
                <Button
                  size="small"
                  disabled={!row.inExploit || isBusy}
                  onClick={() => {
                    setServerId(row.server ? row.server.id : undefined);
                    setAssigning(row);
                  }}
                >
                  {row.server ? "Réaffecter" : "Affecter"}
                </Button>
              </span>
            </Tooltip>
            {row.server && (
              <Popconfirm
                title="Désaffecter cet agent du serveur ? Les métriques ne sont pas modifiées."
                okText="Désaffecter"
                cancelText="Annuler"
                onConfirm={() => handleUnassign(row)}
              >
                <Button size="small" disabled={isBusy}>
                  Désaffecter
                </Button>
              </Popconfirm>
            )}
            <Dropdown menu={{ items: deleteMenu(row) }} trigger={["click"]} disabled={isBusy}>
              <Button size="small" danger disabled={isBusy} aria-label="Nettoyer">
                Nettoyer ▾
              </Button>
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  const columns = baseColumns.map((col) => ({
    ...col,
    width: widths[col.key],
    ...(SORTERS[col.key] && {
      sorter: SORTERS[col.key],
      sortDirections: ["ascend", "descend"],
    }),
    onHeaderCell: () => ({
      width: widths[col.key],
      onResize: (w) => setWidths((prev) => ({ ...prev, [col.key]: w })),
    }),
  }));

  return (
    <React.Fragment>
      <SettingsHeader title="Agents Elastic" />
      {!fleet.available && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Fleet est indisponible : les statuts affichés peuvent être incomplets."
          description={fleet.error || undefined}
        />
      )}
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        {SUMMARY_TILES.map((t) => {
          const value = summary[t.key];
          const unknown = t.needsFleet && !fleet.available;
          const active = t.filter && !!flags[t.filter];
          const color = t.filter ? (value > 0 ? "#d46b08" : "#389e0d") : undefined;
          const tile = (
            <Card
              size="small"
              hoverable={!!t.filter}
              role={t.filter ? "button" : undefined}
              aria-pressed={t.filter ? active : undefined}
              aria-label={`${t.label} : ${unknown ? "inconnu" : value}`}
              tabIndex={t.filter ? 0 : undefined}
              onClick={t.filter ? () => toggleFlag(t.filter) : undefined}
              onKeyDown={
                t.filter
                  ? (e) => (e.key === "Enter" || e.key === " ") && toggleFlag(t.filter)
                  : undefined
              }
              style={active ? { borderColor: "#1890ff", background: "#e6f7ff" } : undefined}
            >
              <Statistic
                title={t.label}
                value={unknown ? "–" : value}
                valueStyle={unknown ? undefined : { color }}
              />
            </Card>
          );
          return (
            <Col key={t.key} xs={12} sm={8} md={4}>
              {unknown ? (
                <Tooltip title="Fleet indisponible : statuts inconnus.">{tile}</Tooltip>
              ) : (
                tile
              )}
            </Col>
          );
        })}
      </Row>
      <Card
        size="small"
        title="Nettoyage recommandé"
        style={{ marginBottom: 12 }}
        extra={
          <Space size="small">
            <Text type="secondary">Inactif depuis plus de</Text>
            <InputNumber
              size="small"
              min={1}
              max={3650}
              value={staleDays}
              onChange={(v) => setStaleDays(v || DEFAULT_STALE_DAYS)}
              aria-label="Seuil d'inactivité en jours"
              style={{ width: 80 }}
            />
            <Text type="secondary">jours</Text>
          </Space>
        }
      >
        {fleet.available ? (
          <Row gutter={[12, 12]}>
            {CLEANUP_RULES.map((r, i) => {
              const count = ruleCounts[r.key] || 0;
              const active = ruleFilter === r.key;
              const phase = phaseMeta(r.phase, fleetWrite);
              const toggle = () => setRuleFilter(active ? undefined : r.key);
              return (
                <Col key={r.key} xs={24} sm={12} lg={8}>
                  <Card
                    size="small"
                    hoverable
                    role="button"
                    aria-pressed={active}
                    aria-label={`${i + 1}. ${r.title} : ${count}`}
                    tabIndex={0}
                    onClick={toggle}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggle()}
                    style={{
                      height: "100%",
                      ...(active ? { borderColor: "#1890ff", background: "#e6f7ff" } : {}),
                    }}
                  >
                    <Space direction="vertical" size={4} style={{ width: "100%" }}>
                      <Space style={{ justifyContent: "space-between", width: "100%" }}>
                        <Tag color={r.color}>
                          {i + 1}. {r.title}
                        </Tag>
                        <Statistic
                          value={count}
                          valueStyle={{
                            fontSize: 22,
                            color: count > 0 ? undefined : "#bfbfbf",
                          }}
                        />
                      </Space>
                      <Space size={[4, 4]} wrap>
                        {r.conditions.map((c) => (
                          <Tag key={c} style={{ marginRight: 0 }}>
                            {c.replace("seuil", `${staleDays} j`)}
                          </Tag>
                        ))}
                      </Space>
                      <Space size={4} wrap>
                        <Text strong>{r.action}</Text>
                        <Tag color={phase.color}>{phase.label}</Tag>
                      </Space>
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>
        ) : (
          <Text type="secondary">
            Recommandations indisponibles tant que Fleet ne répond pas : un agent
            paraîtrait à tort absent de Fleet.
          </Text>
        )}
      </Card>
      <Space wrap style={{ marginBottom: 12 }}>
        <Input
          allowClear
          placeholder="Recherche (agent, hostname, serveur)"
          style={{ width: 300 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          allowClear
          placeholder="Statut Fleet"
          style={{ width: 160 }}
          options={STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
        />
        <Button onClick={load} loading={loading}>
          Rafraîchir
        </Button>
      </Space>
      <div className="scrollable">
        <Table
          rowKey="agent"
          columns={columns}
          components={{ header: { cell: ResizableTitle } }}
          tableLayout="fixed"
          scroll={{ x: "max-content" }}
          dataSource={filtered}
          size="small"
          loading={loading}
          pagination={{ pageSize: 50, hideOnSinglePage: true }}
        />
      </div>
      <Modal
        title="Désenrôler l'agent de Fleet"
        open={!!deleting}
        okText="Désenrôler"
        cancelText="Annuler"
        okButtonProps={{
          danger: true,
          disabled: !deleting || confirmText !== confirmToken(deleting.row),
        }}
        confirmLoading={!!(deleting && busy[deleting.row.agent])}
        onOk={handleFleetDelete}
        onCancel={closeDelete}
        destroyOnClose
      >
        {deleting && (
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Text>
              {deleting.scope === "all"
                ? "L'agent sera désenrôlé de Fleet puis retiré de exploit. Fleet conserve sa fiche en inactif."
                : "L'agent sera désenrôlé de Fleet et n'apparaîtra plus dans exploit. Fleet conserve sa fiche en inactif."}
            </Text>
            <div>Hostname : {deleting.row.hostname || "-"}</div>
            <div>Identifiant : {deleting.row.agent}</div>
            <div>Statut : {statusMeta(deleting.row.status).label}</div>
            <div>
              Dernier check-in :{" "}
              {deleting.row.lastCheckin
                ? `${new Date(deleting.row.lastCheckin).toLocaleString("fr-FR")} (${relativeTime(
                    deleting.row.lastCheckin
                  )})`
                : "inconnu"}
            </div>
            <Alert
              type="warning"
              showIcon
              message="Le désenrôlement est définitif : les clés de l'agent sont révoquées, il devra être réinstallé sur la machine pour revenir."
            />
            <Text>
              Pour confirmer, saisissez{" "}
              <Text code>{confirmToken(deleting.row)}</Text>
            </Text>
            <Input
              aria-label="Confirmation de désenrôlement"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              onPressEnter={() =>
                confirmText === confirmToken(deleting.row) && handleFleetDelete()
              }
            />
          </Space>
        )}
      </Modal>
      <Modal
        title={
          assigning
            ? `${assigning.server ? "Réaffecter" : "Affecter"} l'agent ${
                assigning.hostname || assigning.agent
              }`
            : ""
        }
        open={!!assigning}
        okText="Valider"
        cancelText="Annuler"
        okButtonProps={{ disabled: serverId === undefined }}
        confirmLoading={!!(assigning && busy[assigning.agent])}
        onOk={handleAssign}
        onCancel={closeAssign}
        destroyOnClose
      >
        <Select
          showSearch
          style={{ width: "100%" }}
          placeholder="Choisir un serveur"
          options={serverOptions}
          value={serverId}
          onChange={setServerId}
          optionFilterProp="label"
        />
      </Modal>
    </React.Fragment>
  );
}

export default ElasticAgents;
